import { useState, useEffect } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert, Pagination } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useClientFilter } from '../../hooks/useClientFilter'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import api from '../../services/api'
import { formatDateOnlyEsMX, getDateOnlySortTimestamp } from '../../utils/apiDateFormat'
import BillDetail from './BillDetail'
import ApplyPaymentModal from './ApplyPaymentModal'
import BulkApplyPaymentModal from './BulkApplyPaymentModal'
import { formatCurrency } from '../../utils/formatters'

const getClientName = (client) => {
  if (!client) return 'N/A'
  if (client.razon_social) return client.razon_social
  if (client.business_name) return client.business_name
  const parts = []
  if (client.nombre) parts.push(client.nombre)
  if (client.apellido_paterno) parts.push(client.apellido_paterno)
  if (parts.length > 0) return parts.join(' ')
  return client.name || 'N/A'
}

const BillsList = () => {
  const { user } = useAuth()
  const { clients } = useClientFilter()
  const {
    clientId: filterClient,
    setClientId: setFilterClient,
    selectDisabled,
  } = useSyncedLocalClientId()
  const queryClient = useQueryClient()
  const [showDetail, setShowDetail] = useState(false)
  const [showApplyPayment, setShowApplyPayment] = useState(false)
  const [showBulkApplyPayment, setShowBulkApplyPayment] = useState(false)
  const [selectedBill, setSelectedBill] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterProcesado, setFilterProcesado] = useState('no')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [sortField, setSortField] = useState('emission_date')
  const [sortDirection, setSortDirection] = useState('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, _setItemsPerPage] = useState(10)

  const [selectedBills, setSelectedBills] = useState([])

  const { data: billsData, isLoading, error } = useQuery(
    ['bills', searchTerm, filterStatus, filterClient, startDate, endDate, filterProcesado],
    async () => {
      const params = new URLSearchParams()
      if (searchTerm) params.append('search', searchTerm)
      if (filterStatus !== 'all') params.append('status', filterStatus)
      if (filterClient) params.append('client_id', filterClient)
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      if (filterProcesado !== 'all') params.append('procesado_contable', filterProcesado)
      params.append('ordering', sortDirection === 'desc' ? `-${sortField}` : sortField)

      const response = await api.get(`/financial/bills/?${params.toString()}`)
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  const { data: statsProcesamiento } = useQuery(
    ['bills-stats-procesamiento', filterStatus, filterClient, startDate, endDate],
    async () => {
      const params = new URLSearchParams()
      if (filterStatus !== 'all') params.append('status', filterStatus)
      if (filterClient) params.append('client_id', filterClient)
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      const response = await api.get(`/financial/bills/estadisticas_procesamiento/?${params.toString()}`)
      return response.data
    },
    { enabled: !!user }
  )

  const bills = billsData || []
  const sortedBills = [...bills].sort((a, b) => {
    let aVal = a[sortField]
    let bVal = b[sortField]
    if (sortField === 'emission_date' || sortField === 'due_date') {
      aVal = getDateOnlySortTimestamp(aVal)
      bVal = getDateOnlySortTimestamp(bVal)
    }
    return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1)
  })
  const totalPages = Math.ceil(sortedBills.length / itemsPerPage)
  const paginatedBills = sortedBills.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (field) => {
    if (sortField !== field) return <i className="bi bi-arrow-down-up text-muted" style={{ fontSize: '0.75rem' }}></i>
    return sortDirection === 'asc'
      ? <i className="bi bi-arrow-up text-primary" style={{ fontSize: '0.75rem' }}></i>
      : <i className="bi bi-arrow-down text-primary" style={{ fontSize: '0.75rem' }}></i>
  }

  const toggleSelectBill = (billId) => {
    setSelectedBills(prev =>
      prev.includes(billId) ? prev.filter(id => id !== billId) : [...prev, billId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedBills.length === paginatedBills.length && paginatedBills.length > 0) {
      setSelectedBills([])
    } else {
      setSelectedBills(paginatedBills.map(b => b.id))
    }
  }

  useEffect(() => setCurrentPage(1), [filterStatus, filterClient, searchTerm, startDate, endDate, filterProcesado])
  useEffect(() => setSelectedBills([]), [currentPage])

  const [loadingDetail, setLoadingDetail] = useState(false)
  const handleView = async (bill) => {
    setSelectedBill(bill)
    setShowDetail(true)
    setLoadingDetail(true)
    try {
      const { data } = await api.get(`/financial/bills/${bill.id}/`)
      setSelectedBill(data)
    } catch {
      toast.error('Error al cargar el detalle')
    } finally {
      setLoadingDetail(false)
    }
  }

  const procesarContableMutation = useMutation(
    async (billId) => api.post(`/financial/bills/${billId}/procesar_contable/`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bills')
        queryClient.invalidateQueries('bills-stats-procesamiento')
        queryClient.invalidateQueries('transactions')
        toast.success('Factura procesada contablemente')
        setShowDetail(false)
        setSelectedBill(null)
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Error al procesar contablemente')
      }
    }
  )

  const revertirProcesoContableMutation = useMutation(
    async (billId) => api.post(`/financial/bills/${billId}/revertir_proceso_contable/`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bills')
        queryClient.invalidateQueries('bills-stats-procesamiento')
        queryClient.invalidateQueries('transactions')
        toast.success('Proceso contable revertido correctamente')
        setShowDetail(false)
        setSelectedBill(null)
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'No se puede revertir si la factura tiene pagos aplicados.')
      }
    }
  )

  const handleProcesarContable = (bill) => {
    if (window.confirm(`¿Procesar contablemente la factura ${bill.uuid?.slice(0, 8)}?`)) {
      procesarContableMutation.mutate(bill.id)
    }
  }

  const handleRevertirProcesoContable = (bill) => {
    if (window.confirm(`¿Revertir proceso contable de la factura ${bill.uuid?.slice(0, 8)}? No se puede revertir si tiene pagos aplicados.`)) {
      revertirProcesoContableMutation.mutate(bill.id)
    }
  }

  const deleteMutation = useMutation(
    async (billId) => api.delete(`/financial/bills/${billId}/`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bills')
        toast.success('Factura eliminada')
        setShowDetail(false)
        setSelectedBill(null)
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Error al eliminar')
      }
    }
  )

  const handleDelete = (bill) => {
    const tienePagos = bill.aplicaciones_pago?.some(a => a.estado === 'aplicado') ?? false
    if (tienePagos) {
      toast.error('No se puede eliminar: tiene pagos aplicados.')
      return
    }
    if (window.confirm(`¿Eliminar la factura recibida ${bill.uuid?.slice(0, 8)}?`)) {
      deleteMutation.mutate(bill.id)
    }
  }

  const handleBulkProcesar = async () => {
    if (!window.confirm(`¿Procesar contablemente ${selectedBills.length} factura(s)?`)) return
    try {
      const response = await api.post('/financial/bills/bulk_action/', {
        action: 'procesar_contable',
        bill_ids: selectedBills
      })
      toast.success(response.data.message)
      if (response.data?.errors?.length) response.data.errors.forEach(e => toast.warning(e))
      queryClient.invalidateQueries('bills')
      queryClient.invalidateQueries('bills-stats-procesamiento')
      setSelectedBills([])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al procesar')
    }
  }

  const handleBulkRevertir = async () => {
    if (!window.confirm(`¿Revertir proceso contable de ${selectedBills.length} factura(s)? No se puede revertir si tienen pagos aplicados.`)) return
    try {
      const response = await api.post('/financial/bills/bulk_action/', {
        action: 'revertir_proceso_contable',
        bill_ids: selectedBills
      })
      toast.success(response.data.message)
      if (response.data?.errors?.length) response.data.errors.forEach(e => toast.warning(e))
      queryClient.invalidateQueries('bills')
      queryClient.invalidateQueries('bills-stats-procesamiento')
      setSelectedBills([])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al revertir')
    }
  }

  const handleBulkEliminar = async () => {
    if (!window.confirm(`¿Eliminar ${selectedBills.length} factura(s)? Las que tengan pagos aplicados no se eliminarán.`)) return
    try {
      const response = await api.post('/financial/bills/bulk_action/', {
        action: 'eliminar',
        bill_ids: selectedBills
      })
      toast.success(response.data.message)
      if (response.data.errors?.length) {
        response.data.errors.forEach(e => toast.warning(e))
      }
      queryClient.invalidateQueries('bills')
      setSelectedBills([])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al eliminar')
    }
  }

  const handleRefresh = () => {
    queryClient.invalidateQueries('bills')
    setShowDetail(false)
    setSelectedBill(null)
  }

  const getEstadoProcesamientoBadge = (bill) => {
    const contable = bill.is_processed ? 'Procesado' : 'Pendiente'
    const contableBg = bill.is_processed ? 'success' : 'warning'
    return <Badge bg={contableBg} title="Estado contable">{contable}</Badge>
  }

  const getStatusBadge = (status) => {
    const badges = {
      PENDIENTE: { bg: 'warning', text: 'Pendiente' },
      PARCIAL: { bg: 'info', text: 'Parcial' },
      PAGADA: { bg: 'success', text: 'Pagada' },
      CANCELADA: { bg: 'danger', text: 'Cancelada' }
    }
    const s = (status || '').toUpperCase()
    const b = badges[s] || { bg: 'secondary', text: status || 'N/A' }
    return <Badge bg={b.bg}>{b.text}</Badge>
  }

  const getFormaPagoShort = (codigo) => {
    const map = { '01': 'Efectivo', '02': 'Cheque', '03': 'Transferencia', '04': 'Tarjeta crédito', '05': 'Monedero', '06': 'Dinero electr.', '08': 'Vales', '99': 'Por definir' }
    return map[codigo] || codigo || '—'
  }

  const getCuentasContablesCell = (bill) => {
    const conCuenta = bill.conceptos_con_cuenta ?? 0
    const sinCuenta = bill.conceptos_sin_cuenta ?? 0
    return (
      <span title={`${conCuenta} con cuenta / ${sinCuenta} sin cuenta`}>
        <span className="text-success fw-bold">{conCuenta}</span>
        <span className="text-muted">/</span>
        <span className="text-danger fw-bold">{sinCuenta}</span>
      </span>
    )
  }

  const stats = {
    total: bills.length,
    pendiente: bills.filter(b => b.status === 'PENDIENTE').length,
    parcial: bills.filter(b => b.status === 'PARCIAL').length,
    pagada: bills.filter(b => b.status === 'PAGADA').length,
    totalAmount: bills.reduce((s, b) => s + parseFloat(b.total || 0), 0),
    pendingAmount: bills.reduce((s, b) => s + parseFloat(b.monto_pendiente || 0), 0),
    procesados: statsProcesamiento?.procesados ?? 0,
    noProcesados: statsProcesamiento?.no_procesados ?? 0
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3>Facturas Recibidas</h3>
          <p className="text-muted mb-0">
            <small>Total: {stats.total} | Pendiente: {formatCurrency(stats.pendingAmount)}</small>
          </p>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center border-primary">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-primary">{stats.total}</h5>
              <small className="text-muted">Total</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-warning">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-warning">{stats.pendiente}</h5>
              <small className="text-muted">Pendientes</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-info">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-info">{stats.parcial}</h5>
              <small className="text-muted">Parciales</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-success">
            <Card.Body className="py-2">
              <h5 className="mb-0 text-success">{stats.pagada}</h5>
              <small className="text-muted">Pagadas</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className={`text-center ${filterProcesado === 'no' ? 'border-warning border-2' : 'border-warning'}`} style={{ cursor: 'pointer' }} onClick={() => setFilterProcesado('no')}>
            <Card.Body className="py-2">
              <h5 className="mb-0 text-warning">{stats.noProcesados}</h5>
              <small className="text-muted">Sin procesar</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className={`text-center ${filterProcesado === 'si' ? 'border-success border-2' : 'border-success'}`} style={{ cursor: 'pointer' }} onClick={() => setFilterProcesado('si')}>
            <Card.Body className="py-2">
              <h5 className="mb-0 text-success">{stats.procesados}</h5>
              <small className="text-muted">Procesadas</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <Form.Control
                type="text"
                placeholder="Buscar por UUID, proveedor, cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Control
                type="date"
                placeholder="Desde"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Control
                type="date"
                placeholder="Hasta"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">Todos (Status Pago)</option>
                <option value="PENDIENTE">Pendiente</option>
                <option value="PARCIAL">Parcial</option>
                <option value="PAGADA">Pagada</option>
                <option value="CANCELADA">Cancelada</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select value={filterProcesado} onChange={(e) => setFilterProcesado(e.target.value)}>
                <option value="no">Sin procesar</option>
                <option value="si">Procesadas</option>
                <option value="all">Todas</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterClient}
                onChange={(e) => setFilterClient(e.target.value)}
                disabled={selectDisabled}
                title={selectDisabled ? 'Controlado por el filtro global del encabezado' : undefined}
              >
                <option value="">Todos los clientes</option>
                {(clients || []).map((c) => (
                  <option key={c.id} value={String(c.id)}>{getClientName(c)}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : error ? (
        <Alert variant="danger">Error al cargar facturas recibidas.</Alert>
      ) : paginatedBills.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <p className="text-muted">No hay facturas recibidas.</p>
            <p className="text-muted small">Las facturas recibidas se crean al importar XML desde Contabilidad.</p>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body>
            {selectedBills.length > 0 && (
              <Card className="mb-3 border-primary">
                <Card.Body className="py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><strong>{selectedBills.length}</strong> factura(s) seleccionada(s)</span>
                    <div>
                      <Button variant="outline-info" size="sm" className="me-2" onClick={handleBulkProcesar}>
                        Procesar contablemente
                      </Button>
                      <Button variant="outline-warning" size="sm" className="me-2" onClick={handleBulkRevertir}>
                        Revertir Proceso Contable
                      </Button>
                      <Button variant="outline-success" size="sm" className="me-2" onClick={() => setShowBulkApplyPayment(true)} title="Aplicar pago por el total de cada factura recibida seleccionada (solo las que tengan saldo pendiente)">
                        <i className="bi bi-cash-coin me-1"></i>
                        Pagos
                      </Button>
                      <Button variant="danger" size="sm" className="me-2" onClick={handleBulkEliminar}>
                        Eliminar
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={() => setSelectedBills([])}>
                        Deseleccionar
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

            <Table responsive>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <Form.Check type="checkbox" checked={selectedBills.length === paginatedBills.length && paginatedBills.length > 0} onChange={toggleSelectAll} />
                  </th>
                  <th onClick={() => handleSort('uuid')} style={{ cursor: 'pointer' }}>UUID</th>
                  <th>Serie-Folio</th>
                  <th onClick={() => handleSort('emission_date')} style={{ cursor: 'pointer' }}>Fecha {getSortIcon('emission_date')}</th>
                  <th>RFC Emisor</th>
                  <th>Proveedor</th>
                  <th>RFC Receptor</th>
                  <th>Cliente</th>
                  <th>Metodo pago</th>
                  <th onClick={() => handleSort('total')} style={{ cursor: 'pointer' }}>Total {getSortIcon('total')}</th>
                  <th>Pendiente</th>
                  <th style={{ whiteSpace: 'pre-line' }}>C. Contables{'\n'}Encontradas</th>
                  <th>Estado Procesamiento</th>
                  <th>Status Pago</th>
                  <th>Forma pago</th>
                  <th style={{ minWidth: '180px', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBills.map((bill) => {
                  const tienePagos = bill.aplicaciones_pago?.some(a => a.estado === 'aplicado') ?? false
                  const canDelete = !tienePagos
                  return (
                    <tr key={bill.id} style={{ backgroundColor: selectedBills.includes(bill.id) ? '#e7f3ff' : 'transparent' }}>
                      <td>
                        <Form.Check type="checkbox" checked={selectedBills.includes(bill.id)} onChange={() => toggleSelectBill(bill.id)} />
                      </td>
                      <td><code>{bill.uuid?.slice(0, 12)}...</code></td>
                      <td>{bill.serie_folio || '—'}</td>
                      <td>{bill.emission_date ? formatDateOnlyEsMX(bill.emission_date, {}, 'N/A') : 'N/A'}</td>
                      <td className="text-nowrap">{bill.provider_rfc || '—'}</td>
                      <td>{bill.provider_razon_social || 'N/A'}</td>
                      <td className="text-nowrap">{bill.client_rfc || bill.client?.rfc || '—'}</td>
                      <td>{bill.client_name || getClientName(bill.client) || 'N/A'}</td>
                      <td><small title={bill.metodo_pago === 'PUE' ? 'Pago en una sola exhibición' : bill.metodo_pago === 'PPD' ? 'Pago en parcialidades o diferido' : ''}>{bill.metodo_pago || '—'}</small></td>
                      <td>{formatCurrency(bill.total || 0)}</td>
                      <td>{formatCurrency(bill.monto_pendiente || 0)}</td>
                      <td>{getCuentasContablesCell(bill)}</td>
                      <td>{getEstadoProcesamientoBadge(bill)}</td>
                      <td>{getStatusBadge(bill.status)}</td>
                      <td><small title={bill.forma_pago ? `${bill.forma_pago} - ${getFormaPagoShort(bill.forma_pago)}` : ''}>{bill.forma_pago ? getFormaPagoShort(bill.forma_pago) : '—'}</small></td>
                      <td>
                        <div className="d-flex gap-1 justify-content-center flex-wrap">
                          <Button variant="outline-primary" size="sm" onClick={() => handleView(bill)}>Ver</Button>
                          {(parseFloat(bill.monto_pendiente || 0) > 0.01 ||
                            parseFloat(bill.monto_pagado || 0) > 0.01) &&
                            bill.status !== 'CANCELADA' && (
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => { setSelectedBill(bill); setShowApplyPayment(true) }}
                              title={
                                parseFloat(bill.monto_pendiente || 0) > 0.01
                                  ? 'Aplicar pago o ver pagos asociados'
                                  : 'Ver pagos asociados'
                              }
                            >
                              Pago
                            </Button>
                          )}
                          {!bill.is_processed && (bill.tipo_comprobante || '').toUpperCase() !== 'P' && (
                            <Button variant="outline-info" size="sm" onClick={() => handleProcesarContable(bill)} disabled={procesarContableMutation.isLoading}>Procesar Contable</Button>
                          )}
                          {(bill.tipo_comprobante || '').toUpperCase() === 'P' && (
                            <Button variant="outline-secondary" size="sm" disabled title="Complemento de pago: el asiento se genera al vincular/aplicar el pago a la factura PPD origen">
                              Complemento (REP)
                            </Button>
                          )}
                          {bill.is_processed && (bill.tipo_comprobante || '').toUpperCase() !== 'P' && (
                            <Button variant="outline-warning" size="sm" onClick={() => handleRevertirProcesoContable(bill)} disabled={revertirProcesoContableMutation.isLoading} title="Revertir proceso contable. No se puede si tiene pagos aplicados.">Revertir Proceso Contable</Button>
                          )}
                          {canDelete && (
                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(bill)} disabled={deleteMutation.isLoading}>Eliminar</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <Pagination className="mb-0 mt-3 justify-content-center">
                <Pagination.Prev disabled={currentPage <= 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} />
                <Pagination.Item disabled>{currentPage} / {totalPages}</Pagination.Item>
                <Pagination.Next disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} />
              </Pagination>
            )}
          </Card.Body>
        </Card>
      )}

      <Modal show={showDetail} onHide={() => { setShowDetail(false); setSelectedBill(null) }} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Detalle Factura Recibida</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetail ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : selectedBill ? (
            <BillDetail bill={selectedBill} onRefresh={handleRefresh} onBillUpdated={(updated) => setSelectedBill(updated)} />
          ) : null}
        </Modal.Body>
      </Modal>

      <ApplyPaymentModal
        show={showApplyPayment}
        onHide={() => { setShowApplyPayment(false); setSelectedBill(null) }}
        bill={selectedBill}
        onSuccess={handleRefresh}
      />

      <BulkApplyPaymentModal
        show={showBulkApplyPayment}
        onHide={() => setShowBulkApplyPayment(false)}
        items={sortedBills.filter(b => selectedBills.includes(b.id))}
        isBill
        onSuccess={() => {
          queryClient.invalidateQueries('bills')
          queryClient.invalidateQueries('payments')
          setShowBulkApplyPayment(false)
          setSelectedBills([])
          handleRefresh()
        }}
      />
    </div>
  )
}

export default BillsList
